import dayjs from 'dayjs';
import gql from 'graphql-tag';
import { useMemo } from 'react';

import type { AudienceOptionType } from '@/api/audience';
import { useDisplayNames } from '@/api/common/commonAPI';
import { useCurrentInstance } from '@/api/organization';
import { getVariablesFromTableParamsNew } from '@/api/util/getVariablesFromTableParams';
import { type QueryState, type SolQueryParamsNew } from '@/components/InstantSearch';
import { DateISOFormat } from '@/constants/formats';
import { useSolQuery } from '@/hooks/useSolQuery';

import {
  type OpportunityByIdGQLResponse,
  type OpportunityInfluencedGQLResponse,
  type OpportunityInfluencedListGQLResponse,
  type OpportunityKpiMetricsGQLResponse,
  type OpportunityOverviewGQLResponse,
  type OpportunityOverviewKPIMetricsGQLResponse,
  type OpportunityOverviewTrendGQLResponse,
  type OpportunityStatusOldType,
  type OpportunityType,
} from './opportunityType';

export const useOpportunityOverviewKPIMetrics = (
  startDate?: string,
  endDate?: string,
  audience?: AudienceOptionType,
) => {
  const canExecuteQuery = !!startDate && !!endDate;
  const { data, isLoading, error } = useSolQuery<OpportunityOverviewKPIMetricsGQLResponse>({
    query:
      canExecuteQuery &&
      gql`
        query OpportunitiesKpi($startDate: DateTime!, $endDate: DateTime!, $audienceIds: [String]) {
          visualization {
            opportunitiesKpi(startDate: $startDate, endDate: $endDate, audienceIds: $audienceIds) {
              averageContractValue
              averageDaysToClose
              closedLostOpportunityCount
              closedWonAmount
              closedWonCount
              pipelineAmount
              pipelineCount
              pipelineToClosedWonRatio
              winRate
            }
          }
        }
      `,
    variables: {
      startDate: startDate + 'T00:00:00Z',
      endDate: endDate + 'T23:59:59Z',
      audienceIds: audience?.id,
    },
  });

  return {
    opportunityOverviewKPIMetrics: data?.visualization.opportunitiesKpi,
    isLoading,
    error,
  };
};

export const useOpportunityOverviewTrend = (
  startDate?: string,
  endDate?: string,
  audience?: AudienceOptionType,
) => {
  const canExecuteQuery = !!startDate && !!endDate;
  const { data, isLoading, error } = useSolQuery<OpportunityOverviewTrendGQLResponse>({
    query:
      canExecuteQuery &&
      gql`
        query OpportunityTrend($startDate: DateTime!, $endDate: DateTime!, $audienceIds: [String]) {
          visualization {
            opportunityTrend(startDate: $startDate, endDate: $endDate, audienceIds: $audienceIds) {
              count
              revenue
              status
              sundayOfWeek
            }
          }
        }
      `,
    variables: {
      startDate: startDate + 'T00:00:00Z',
      endDate: endDate + 'T23:59:59Z',
      audienceIds: audience?.id,
    },
  });

  return {
    opportunityTrend: data?.visualization.opportunityTrend,
    isLoading,
    error,
  };
};

export const useOpportunitiesList = (
  tableParams?: SolQueryParamsNew,
  startDate?: string,
  endDate?: string,
  audience?: AudienceOptionType,
) => {
  const canExecuteQuery = tableParams && startDate?.length && endDate?.length;
  const { data, isLoading, error } = useSolQuery<OpportunityOverviewGQLResponse>(
    {
      query:
        !!canExecuteQuery &&
        gql`
          query OpportunityOverview(
            $startDate: DateTime!
            $endDate: DateTime!
            $page: Pagination!
            $sort: [SortParamInput]!
            $filter: [FilterParamInput]!
            $searchQuery: String
            $audienceIds: [String]
          ) {
            visualization {
              opportunityOverview(
                startDate: $startDate
                endDate: $endDate
                page: $page
                sort: $sort
                filter: $filter
                searchQuery: $searchQuery
                audienceIds: $audienceIds
              ) {
                edges {
                  node {
                    account {
                      id
                      name
                      tld
                      company {
                        id
                        name
                        tld
                      }
                    }
                    amount
                    closeDate
                    daysToClose
                    id
                    name
                    openDate
                    sfUrl
                    status
                  }
                }
                totalEdges
              }
            }
          }
        `,
      variables: {
        ...getVariablesFromTableParamsNew(tableParams),
        startDate: startDate + 'T00:00:00Z',
        endDate: endDate + 'T23:59:59Z',
        audienceIds: audience?.id,
      },
    },
    {
      keepPreviousData: true,
    },
  );

  return {
    opportunityOverview: data?.visualization.opportunityOverview.edges.map((edge) => edge.node),
    totalResults: data?.visualization.opportunityOverview.totalEdges,
    isLoading,
    error,
  };
};

export const useOpportunityById = (opportunityId?: string) => {
  const { instance } = useCurrentInstance();
  const canExecuteQuery = !!opportunityId;

  const { data, isLoading, error } = useSolQuery<OpportunityByIdGQLResponse>({
    query:
      canExecuteQuery &&
      gql`
        query GetOpportunityById($id: String!) {
          opportunities {
            getById(id: $id) {
              id
              name
              openDate
              closeDate
              daysToClose
              amount
              sfUrl
              account {
                id
                name
                tld
                company {
                  id
                  name
                  tld
                }
              }
              status
            }
          }
        }
      `,
    variables: {
      id: opportunityId,
    },
  });

  const opportunity = useMemo(() => {
    if (!data) {
      return;
    }

    const {
      getById: { openDate, closeDate },
    } = data.opportunities;

    const influenceStartDate = dayjs(openDate)
      .subtract(instance?.pipelineReport?.influenceWindow ?? 90, 'day')
      .format(DateISOFormat);

    return {
      ...data.opportunities.getById,
      influenceStartDate,
      openDate: dayjs(openDate).format(DateISOFormat),
      closeDate: closeDate ? dayjs(closeDate).format(DateISOFormat) : null,
    } satisfies OpportunityType;
  }, [data, instance?.pipelineReport?.influenceWindow]);

  return {
    opportunity,
    isLoading,
    error,
  };
};

export const useOpportunityKpiMetrics = (opportunityId?: string) => {
  const { data, isLoading, error } = useSolQuery<OpportunityKpiMetricsGQLResponse>({
    query:
      !!opportunityId &&
      gql`
        query OpportunityKpiMetrics($opportunityId: [String!]!) {
          visualization {
            opportunityDetailKpi(opportunityId: $opportunityId) {
              opportunityDaysOpen
              opportunityNonDigitalEvents
              opportunityVisits
              revenue
              vendorCount
            }
          }
        }
      `,
    variables: {
      opportunityId,
    },
  });

  return {
    opportunityKpiMetrics: data?.visualization.opportunityDetailKpi,
    isLoading,
    error,
  };
};

export const useOpportunityStatusOptions = () => {
  const { displayNames } = useDisplayNames(['CLOSED_WON', 'CLOSED_LOST', 'PIPELINE']);

  return {
    opportunityStatusOptions: displayNames,
  };
};

export const useOpportunityInfluenced = (
  {
    startDate,
    endDate,
    vendorIds,
    campaignIds,
    pixelIds,
    audienceIds,
    groupBy,
  }: {
    startDate?: string;
    endDate?: string;
    vendorIds?: string[];
    campaignIds?: string[];
    pixelIds?: string[];
    audienceIds?: string[];
    groupBy?: 'CAMPAIGN' | 'VENDOR' | 'TAG';
  },
  canExecuteQuery = true,
) => {
  const hasDates = startDate?.length && endDate?.length;
  const hasIds = pixelIds?.length || campaignIds?.length || vendorIds?.length;
  const canExecute = canExecuteQuery && hasDates && hasIds;

  const { data, isLoading, error } = useSolQuery<OpportunityInfluencedGQLResponse>({
    query:
      !!canExecute &&
      gql`
        query OpportunityInfluenced(
          $startDate: DateTime
          $endDate: DateTime
          $groupBy: OpportunityInfluencedGroupBy
          $vendorIds: [String]
          $campaignIds: [String]
          $pixelIds: [String]
          $audienceIds: [String]
        ) {
          visualization {
            opportunityInfluenced(
              startDate: $startDate
              endDate: $endDate
              groupBy: $groupBy
              vendorIds: $vendorIds
              campaignIds: $campaignIds
              pixelIds: $pixelIds
              audienceIds: $audienceIds
            ) {
              pipeline {
                data {
                  sundayOfWeek
                  opportunities
                  revenue
                }
                totalOpportunities
                totalRevenue
              }
              closedWon: recentDeals {
                data {
                  sundayOfWeek
                  opportunities
                  revenue
                }
                totalOpportunities
                totalRevenue
              }
            }
          }
        }
      `,
    variables: {
      startDate: startDate + 'T00:00:00Z',
      endDate: endDate + 'T23:59:59Z',
      groupBy,
      audienceIds,
      pixelIds,
      campaignIds,
      vendorIds,
    },
  });

  return {
    opportunityInfluenced: data?.visualization.opportunityInfluenced[0],
    isLoading,
    error,
  };
};

export const useOpportunityInfluencedList = (
  pageState: Omit<QueryState, 'search' | 'filters'>,
  opportunityType: OpportunityStatusOldType,
  {
    startDate,
    endDate,
    vendorIds,
    campaignIds,
    pixelIds,
    audienceIds,
  }: {
    startDate?: string;
    endDate?: string;
    vendorIds?: string[];
    campaignIds?: string[];
    pixelIds?: string[];
    audienceIds?: string[];
  },
  canExecuteQuery = true,
) => {
  const hasDates = startDate?.length && endDate?.length;
  const hasIds = pixelIds?.length || campaignIds?.length || vendorIds?.length;
  const canExecute = canExecuteQuery && hasDates && hasIds;

  // The 'date' column doesn't actually exist for an opportunity. We'll change it to either an open
  // or closed date based on the influenceType.
  if (pageState.sortBy === 'date') {
    pageState = {
      ...pageState,
      sortBy: opportunityType === 'pipeline' ? 'openDate' : 'closeDate',
    };
  }

  const { data, isLoading, error } = useSolQuery<OpportunityInfluencedListGQLResponse>(
    {
      query:
        !!canExecute &&
        gql`
          query OpportunityInfluencedList(
            $page: Pagination!
            $sort: [SortParamInput]!
            $startDate: DateTime
            $endDate: DateTime
            $vendorIds: [String]
            $campaignIds: [String]
            $pixelIds: [String]
            $audienceIds: [String]
            $opportunityType: OpportunityType
          ) {
            visualization {
              opportunityInfluencedList(
                page: $page
                sort: $sort
                startDate: $startDate
                endDate: $endDate
                vendorIds: $vendorIds
                campaignIds: $campaignIds
                pixelIds: $pixelIds
                audienceIds: $audienceIds
                opportunityType: $opportunityType
              ) {
                edges {
                  node {
                    id
                    name
                    accountCompanyName
                    metricsImpressions
                    metricsVisits
                    openDate
                    closeDate
                    amount
                  }
                }
                totalEdges
              }
            }
          }
        `,
      variables: {
        opportunityType: opportunityType === 'pipeline' ? 'PIPELINE' : 'RECENTDEAL',
        startDate: startDate + 'T00:00:00Z',
        endDate: endDate + 'T23:59:59Z',
        vendorIds,
        campaignIds,
        pixelIds,
        audienceIds,
        page: {
          offset: (pageState.page - 1) * pageState.size,
          limit: pageState.size,
        },
        sort: {
          field: pageState.sortBy,
          direction: pageState.sortOrder,
        },
      },
    },
    {
      keepPreviousData: true,
    },
  );

  return {
    opportunitiesListData: data?.visualization.opportunityInfluencedList.edges.map(
      (edge) => edge.node,
    ),
    totalResults: data?.visualization.opportunityInfluencedList.totalEdges,
    isLoading,
    error,
  };
};
